import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Seo } from "../components/Seo";

export default function PortalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);
  const { setUser } = useSite();
  const navigate = useNavigate();

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0B1B3D] flex items-center justify-center px-5 relative grain">
      <Seo noindex title="Owner Portal | Noyola Construction LLC" path="/portal/login" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-10">
          <span className="w-12 h-12 bg-[#005AE0] flex items-center justify-center"><HardHat className="w-6 h-6 text-white" /></span>
          <div className="font-heading text-white">
            <p className="font-extrabold text-lg leading-none">NOYOLA</p>
            <p className="text-[11px] tracking-[0.28em] opacity-70">CONSTRUCTION LLC</p>
          </div>
        </div>
        {forgot ? (
          <form onSubmit={submitForgot} className="bg-white p-8 md:p-10" data-testid="forgot-password-form">
            <div className="flex items-center gap-2 mb-8">
              <Lock className="w-4 h-4 text-[#005AE0]" />
              <h1 className="font-heading font-bold text-[#0B1B3D] text-xl">Reset Your Password</h1>
            </div>
            {sent ? (
              <p className="bg-green-50 border border-green-200 text-green-700 text-sm p-4 mb-5" data-testid="forgot-sent-msg">
                If that email exists, a reset link has been sent. Check your inbox (valid for 1 hour).
              </p>
            ) : (
              <>
                {error && <p className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-5">{error}</p>}
                <label className="field-label">Account Email</label>
                <input className="field mb-8" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="btn-primary w-full">
                  {busy ? "Sending…" : "Send Reset Link"}
                </button>
              </>
            )}
            <button type="button" onClick={() => { setForgot(false); setSent(false); setError(""); }}
              className="block w-full text-center text-sm text-[#005AE0] mt-5" data-testid="back-to-login-btn">
              Back to login
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="bg-white p-8 md:p-10" data-testid="portal-login-form">
          <div className="flex items-center gap-2 mb-8">
            <Lock className="w-4 h-4 text-[#005AE0]" />
            <h1 className="font-heading font-bold text-[#0B1B3D] text-xl">Business Portal Login</h1>
          </div>
          {error && <p className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-5" data-testid="login-error">{error}</p>}
          <label className="field-label">Email</label>
          <input className="field mb-5" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <label className="field-label">Password</label>
          <input className="field mb-8" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <button type="submit" disabled={busy} data-testid="login-submit-btn" className="btn-primary w-full">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <button type="button" onClick={() => { setForgot(true); setError(""); }}
            className="block w-full text-center text-sm text-[#005AE0] mt-5" data-testid="forgot-password-link">
            Forgot password?
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
